/**
 * Stone Investment Page Table of Contents
 * 
 * Sidebar TOC for long pages with Stone Investment's luxury aesthetic:
 * - Sticky positioning
 * - Active section highlighting
 * - Smooth scroll links
 */

'use client';

import { useState, useEffect } from 'react';

export interface TocItem {
  id: string;
  text: string;
  level: number;
}

export interface StonePageTableOfContentsProps {
  /**
   * Table of contents items (alias for headings for core compatibility)
   */
  items?: TocItem[];
  /**
   * Table of contents headings (core interface compatible)
   */
  headings?: TocItem[];
  /**
   * Active heading ID (core interface compatible)
   */
  activeId?: string;
  /**
   * Section title
   */
  title?: string;
  /**
   * Offset for active section detection (px from top)
   */
  offsetTop?: number;
  /**
   * Custom className
   */
  className?: string;
}

/**
 * StonePageTableOfContents - Sidebar TOC
 */
export function StonePageTableOfContents({
  items,
  headings,
  activeId: externalActiveId,
  title = 'On This Page',
  offsetTop = 100,
  className = '',
}: StonePageTableOfContentsProps) {
  // Support both 'items' and 'headings' props for compatibility
  const tocItems = items || headings || [];
  const [activeId, setActiveId] = useState<string>(externalActiveId || tocItems[0]?.id || '');
  
  // Sync with external activeId if provided
  useEffect(() => {
    if (externalActiveId) {
      setActiveId(externalActiveId);
    }
  }, [externalActiveId]);
  
  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + offsetTop;
      
      // Find the current active section
      for (let i = tocItems.length - 1; i >= 0; i--) {
        const element = document.getElementById(tocItems[i]!.id);
        if (element && element.offsetTop <= scrollPosition) {
          setActiveId(tocItems[i]!.id);
          break;
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [tocItems, offsetTop]);
  
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const top = element.offsetTop - offsetTop + 20;
      window.scrollTo({ top, behavior: 'smooth' });
      setActiveId(id);
    }
  };
  
  if (tocItems.length === 0) return null;
  
  return (
    <nav className={`sticky top-24 ${className}`}>
      {/* Title */}
      <h4 className="text-[10px] uppercase tracking-[0.3em] text-stone-500 font-sans mb-4">
        {title}
      </h4>
      
      {/* Items */}
      <ul className="space-y-2">
        {tocItems.map((item) => {
          const isActive = item.id === activeId;
          const indent = (item.level - 1) * 12;
          
          return (
            <li key={item.id} style={{ paddingLeft: indent }}>
              <a
                href={`#${item.id}`}
                onClick={(e) => handleClick(e, item.id)}
                className={`
                  block py-1.5 text-sm font-sans transition-colors border-l-2
                  ${isActive 
                    ? 'text-stone-900 border-stone-900 pl-4 font-medium' 
                    : 'text-stone-500 border-transparent pl-4 hover:text-stone-700 hover:border-stone-300'
                  }
                `}
              >
                {item.text}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export default StonePageTableOfContents;
